import { logging, API_BASE_URL } from '@/config/env';
import type {
  ContactForm,
  ContactFormListResponse,
  ApiContactFormListResponse,
  ContactFormQueryParams,
} from './contactUs.dto';

// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class ContactFormService {
  private baseUrl = API_BASE_URL;
  private logoutCallback?: () => void;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }
      } catch {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  // Contact Form Management APIs
  async getContactForms(
    token: string,
    params: ContactFormQueryParams = {}
  ): Promise<ContactFormListResponse> {
    customLog('API Service: getContactForms called');
    customLog('API Service: Token:', token ? 'Present' : 'Missing');
    customLog('API Service: Params:', params);

    const queryString = new URLSearchParams();

    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status.toString());
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/contact${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);
    customLog(
      'API Service: Authorization header:',
      `Bearer ${token.substring(0, 20)}...`
    );

    const response = await this.request<ApiContactFormListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response keys:', Object.keys(response));
    customLog('response status:', response.status as unknown);
    customLog('response message:', response.message as unknown);
    customLog('response data:', response.data);
    customLog('response.data type:', typeof response.data);

    // Check if response.data exists
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as unknown as
      | ContactForm[]
      | {
          data: ContactForm[];
          meta?: {
            total: number;
            page: number;
            lastPage: number;
            limit: number;
          };
        };

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: response.total || 0,
          page: response.page || 1,
          lastPage: response.lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || [],
      meta: responseData.meta || {
        total: response.total || 0,
        page: response.page || 1,
        lastPage: response.lastPage || 1,
        limit: params.limit || 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getContactFormById(token: string, id: number): Promise<ContactForm> {
    customLog('API Service: getContactFormById called with ID:', id);

    const response = await this.request<{
      status: boolean;
      message: string;
      data: ContactForm;
    }>(`/admin/contact/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async deleteContactForm(
    token: string,
    id: number
  ): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/contact/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }
}

// Export a singleton instance
export const contactFormService = new ContactFormService();
